// filepath: apps/web/src/features/observability/widgets/InstanceCountWidget.tsx
//
// FR-UI-DASH-1 AC-1 widget 1 -- KPI tile for active / failed / completed
// process-instance counts in the last 24 hours.
//
// INV-9: all chrome is rendered as text nodes.
// INV-1: tenant scoping comes from the parent page's `summary`
// envelope (server-filtered); no client-side tenant filter.
// AC-5: drill-down navigates to the failed-instance dashboard
// (FR-UI-PROC-1) with the time range + metric filter applied via
// URLSearchParams -- typed `MetricId` ensures no user-controlled
// free text flows into the URL.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { DrilldownLink } from "../DrilldownLink";
import type { TenantMetricsSummary } from "../types";

export interface InstanceCountWidgetProps {
  summary: TenantMetricsSummary;
  /** Optional drill-down override (tests / parent-supplied router). */
  onDrillDown?: (params: { from: string; to: string; metric: string }) => void;
}

export function InstanceCountWidget({
  summary,
  onDrillDown,
}: InstanceCountWidgetProps): React.ReactElement {
  const { t } = useTranslation();

  const toIso = new Date().toISOString();
  // Last 24h window -- the widget aggregates the last 24h
  // implicitly (the server's `instance_count` is a 24h-rolling
  // total per FR-OBS-3). We surface a fixed window here so the
  // drill-down query carries an explicit filter.
  const fromIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  return (
    <article
      className="observability-widget observability-widget-instance-count"
      data-testid="observability-widget-instance-count"
      aria-labelledby="observability-widget-instance-count-title"
      data-tenant-scope="self"
    >
      <h2
        id="observability-widget-instance-count-title"
        className="observability-widget-title"
      >
        {t(
          "observability.dashboard.widget.instance_count.title",
          "Process instances (last 24h)",
        )}
      </h2>
      <dl className="observability-widget-kpis">
        <div className="observability-widget-kpi">
          <dt className="observability-widget-kpi-label">
            {t(
              "observability.dashboard.widget.instance_count.active",
              "Active",
            )}
          </dt>
          <dd
            className="observability-widget-kpi-value"
            data-testid="observability-widget-instance-count-active"
          >
            {summary.instance_count - summary.error_count}
          </dd>
        </div>
        <div className="observability-widget-kpi">
          <dt className="observability-widget-kpi-label">
            {t(
              "observability.dashboard.widget.instance_count.failed",
              "Failed",
            )}
          </dt>
          <dd
            className="observability-widget-kpi-value"
            data-testid="observability-widget-instance-count-failed"
          >
            {summary.error_count}
          </dd>
        </div>
        <div className="observability-widget-kpi">
          <dt className="observability-widget-kpi-label">
            {t(
              "observability.dashboard.widget.instance_count.completed",
              "Completed",
            )}
          </dt>
          <dd
            className="observability-widget-kpi-value"
            data-testid="observability-widget-instance-count-completed"
          >
            {Math.max(summary.instance_count - summary.error_count, 0)}
          </dd>
        </div>
      </dl>
      <DrilldownLink
        from={fromIso}
        to={toIso}
        metric="instance_count"
        onNavigate={onDrillDown}
        label={t(
          "observability.dashboard.widget.drill_down",
          "View failed instances",
        )}
      />
    </article>
  );
}
